import React, { useCallback, useEffect, useState } from 'react';
import { ReactComponent as LoaderSecondary } from '../../../assets/SpinnerBlack.svg';
import { useAuth } from '../../../context/AuthContext';
import { useBrandContext } from '../../../context/BrandContext';

import { getCurrentPriceProfilePeriod } from '../../../services/chargerStationService';
import { getBalance } from '../../../services/userInformationService';
import { formatCurrency, formatHoursMinutes } from '../../../utils/format';
import Modal from '../../modal/Modal';
import ValueItem from '../../valueItem/ValueItem';
import './ToastStartCharging.css';
import { Props } from './interfaces';
import { CardCupomApplied } from '../../../components/cardCupomApplied/CardCupomApplied';
import Coupons from '../../../features/coupons/Coupons';
import { useCouponStore } from '../../../hooks/useCouponStore';
import api from '../../../axios/axiosConfig';

const ToastStartCharging: React.FC<Props> = ({
  isModalOpen,
  isLoading,
  setIsModalOpen,
  setIsLoading,
  allowButtonClosed,
  handleStartCharging,
  chargerData,
  chargerId,
  setSelectedVehicle,
  selectedVehicle,
  chargerSiteId,
  chargerSiteName,
}) => {
  const [balance, setBalance] = useState<any>(0);
  const [priceData, setpriceData] = useState<any>();
  const [formattedTime, setFormattedTime] = useState<string>('');
  const [vehicles, setVehicles] = useState<any[]>();
  const [errorMessage, setErrorMessage] = useState<string>('');

  const [isSelectingCoupon, setIsSelectingCoupon] = useState<boolean>(false);

  const { couponData, setCouponData, setSelectedCoupon } = useCouponStore();

  const authContext = useAuth();
  const brandContext = useBrandContext();

  const PERIOD_PRICE_TIME_OVERSTAY_MINUTES =
    priceData?.priceProfilePeriodPriceTimeOverstay / 60;

  const getVehicles = useCallback(async (): Promise<any> => {
    try {
      const response = await api.get(`/UserVehicles`);
      return response.data;
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      return [];
    }
    // authContext permanece na dep so para refazer a busca quando a sessao
    // muda - o token em si vem do header default da instancia do axios.
  }, [authContext]);

  const handleVehicleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedVehicle(e.target.value);
    setErrorMessage('');
  };

  const handleStartChargingWithValidation = () => {
    if ((vehicles?.length || 0) > 1 && !selectedVehicle) {
      setErrorMessage('É necessário selecionar um veículo.');
    } else {
      handleStartCharging();
    }
  };

  const handleCouponSelect = (couponData: any) => {
    if (!setSelectedCoupon) return;
    setCouponData(couponData);
    setSelectedCoupon(couponData.udcId as string);
    setIsSelectingCoupon(false);
  };

  const toggleCouponSelect = () => {
    setIsSelectingCoupon(true);
  };

  // selecionar o primeiro cupom que tiver saldo ainda para utilizar
  useEffect(() => {
    const getCouponList = async () => {
      if (isModalOpen) {
        setIsLoading(true);
        try {
          const { data = {} } = await api.get(`/UserDiscountCoupon`, {
            params: {
              status: 'active',
              page: 1,
              pageSize: 1000,
              search: '',
              dcType: 'SalesCoupon',
              verifySiteId: chargerSiteId,
            },
          });
          if (data?.items?.length) {
            const toEpochOrInf = (iso?: string) => {
              const t = Date.parse(iso ?? '');
              return Number.isNaN(t) ? Infinity : t;
            };

            const preferedCoupon =
              (data?.items as any[] | undefined)
                ?.filter((c) => c?.availableSite)
                .reduce<any>((best, current) => {
                  if (!best) return current;

                  const bestParam = best.dcParameter ?? -Infinity;
                  const currParam = current.dcParameter ?? -Infinity;

                  // 1) maior dcParameter vence
                  if (currParam !== bestParam) {
                    return currParam > bestParam ? current : best;
                  }

                  // 2) empate: menor validade (data mais próxima) vence
                  const bestExp = toEpochOrInf(best.dcValidTo);
                  const currExp = toEpochOrInf(current.dcValidTo);
                  return currExp < bestExp ? current : best;
                }, null) ?? null;

            if (preferedCoupon) {
              handleCouponSelect(preferedCoupon);
            }
          }
        } catch (error: any) {
          console.error(error);
        } finally {
          setTimeout(() => {
            setIsLoading(false);
          }, 250);
        }
      }
    };
    getCouponList();
  }, [isModalOpen]);

  useEffect(() => {
    if (priceData?.priceProfilePeriodOverstayGracePeriod) {
      const formatted = formatHoursMinutes(
        priceData.priceProfilePeriodOverstayGracePeriod
      );
      setFormattedTime(formatted);
    }
  }, [priceData?.priceProfilePeriodOverstayGracePeriod]);

  useEffect(() => {
    (async () => {
      try {
        const data = await getBalance();
        setBalance(formatCurrency(Number(data?.uwalBalance)));
      } catch (e) {
        setBalance(formatCurrency(0));
      }
    })();

    if (chargerData) {
      setpriceData(chargerData);
    } else if (chargerId) {
      (async () => {
        try {
          const data = await getCurrentPriceProfilePeriod(chargerId);
          setpriceData(data);
        } catch (e) {
          console.log('ERRO GET PRICE: ', e);
        }
      })();
    }
  }, []);

  useEffect(() => {
    const loadVehicles = async () => {
      const vehicles = await getVehicles();
      setSelectedVehicle(vehicles.length === 1 ? vehicles[0].uvId : null);
      if (vehicles.length > 1) setVehicles(vehicles);
    };

    loadVehicles();
  }, [getVehicles]);

  useEffect(() => {
    if (!isSelectingCoupon) return;
    const handlePopState = () => {
      setIsSelectingCoupon(false);
      window.history.pushState(null, '', window.location.href);
    };

    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isSelectingCoupon, setIsSelectingCoupon]);

  if (isSelectingCoupon) {
    return (
      <div
        style={{
          position: 'fixed',
          zIndex: 100,
          height: '100vh',
          width: '100vw',
          overflowY: 'scroll',
          top: 0,
          left: 0,
          backgroundColor: '#fff',
        }}
      >
        <Coupons
          fromRechargeStart={true}
          setCouponScreenActive={setIsSelectingCoupon}
          handleCouponSelect={handleCouponSelect}
          chargerSiteId={chargerSiteId}
          chargerSiteName={chargerSiteName as string}
        />
      </div>
    );
  }

  return (
    <>
      <Modal
        allowButtonClosed={allowButtonClosed}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setCouponData({});
          setSelectedCoupon('');
        }}
        textEnterButton='Iniciar'
        handleEnterButton={handleStartChargingWithValidation}
        isLoadingEnterButton={isLoading}
        styleEnterButton={{ backgroundColor: `${brandContext.brandBgColor}` }}
        disableEnterButton={vehicles && vehicles.length > 1 && !selectedVehicle}
      >
        <div className='toast-start-charging-value-content'>
          <p>Saldo Disponível</p>
          <div className='toast-start-charging-value'>
            {balance ? balance : <LoaderSecondary className='spinner' />}
          </div>
        </div>

        <div>
          <CardCupomApplied onClick={toggleCouponSelect} data={couponData} />
        </div>

        <div className='toast-start-charging-content'>
          <b className='toast-start-charging-title'>
            Iniciar a recarga do seu veículo?
          </b>

          {(vehicles?.length || 0) > 1 && (
            <div className='select__container select__container--brand'>
              <select
                id='select-brand'
                className='select__input'
                value={selectedVehicle || ''}
                onChange={handleVehicleChange}
              >
                <option value=''>Selecione o veículo</option>
                {vehicles?.map((vehicle) => (
                  <option key={vehicle.uvId} value={vehicle.uvId}>
                    {vehicle.uvName}
                  </option>
                ))}
              </select>
              {errorMessage && vehicles && vehicles.length > 1 && (
                <div className='error-message'>{errorMessage}</div>
              )}
            </div>
          )}

          <div className='toast-start-chargin-time'>
            Você terá 1 minuto para conectar o plug no veículo.
          </div>

          {priceData && priceData?.priceProfilePeriodFreeCharge ? (
            <h4 className='toast-start-charging__message--free'>
              Recarga gratuita!{' '}
            </h4>
          ) : (
            priceData && (
              <div className='toast-start-charging-info-values'>
                <div className='value-items-container'>
                  <ValueItem
                    data={priceData?.priceProfilePeriodPriceKwh}
                    paramType=' por kWh utilizado.'
                  />
                  <ValueItem
                    data={priceData?.priceProfilePeriodPriceTimeCharging}
                    paramType='/h'
                  />
                  <ValueItem
                    data={priceData?.priceProfilePeriodTransactionPrice}
                    paramType='de taxa de serviço do carregador.'
                  />
                  <ValueItem
                    data={priceData?.priceProfilePeriodPriceTimeParking}
                    paramType='/h Estacionamento'
                  />
                  <ValueItem
                    data={priceData?.priceProfilePeriodPriceTimePlugged}
                    paramType=' por hora enquanto o veículo estiver utilizando o carregador.'
                  />
                </div>

                {priceData?.priceProfilePeriodPriceTimeOverstay != null &&
                  priceData?.priceProfilePeriodPriceTimeOverstay > 0 && (
                    <>
                      <div className='custom-paragraphs'>
                        A tolerância após finalizar a recarga será de
                        {` ${formattedTime} `} com o veículo conectado ao
                        carregador.
                      </div>
                      <div className='custom-paragraphs'>
                        Após esse período será cobrada a taxa de{' '}
                        {`${formatCurrency(
                          PERIOD_PRICE_TIME_OVERSTAY_MINUTES
                        )}`}{' '}
                        por minuto de permanência.
                      </div>
                    </>
                  )}

                <ValueItem
                  data={priceData?.priceProfilePeriodOverstayPenalty}
                  paramType='/penalidade por ultrapassar o periodo de tolerancia'
                />
                <ValueItem
                  data={priceData?.priceProfilePeriodDiscountTotal}
                  paramType='/desconto sobre a cobrança total no final'
                />
                <ValueItem
                  data={priceData?.priceProfilePeriodParkingGracePeriod}
                  paramType='/falta definir texto'
                />
              </div>
            )
          )}
        </div>
      </Modal>
    </>
  );
};

export default ToastStartCharging;
